import { ReactComponent as LogoIdA } from "@/components/layout/logo-ida.svg";
import { PoweredBy } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { CircleAlert } from "lucide-react";

/**
 * Le pagine di sistema che non hanno il menu: il profilo che non si legge, il ruolo senza sezioni
 * (blocco 4 del pacchetto, B4-errore-accesso). Dicono cosa è successo e cosa fare dopo.
 */
const giornoEOra = (d) => new Intl.DateTimeFormat("it-IT", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(d);
const soloOra = (d) => new Intl.DateTimeFormat("it-IT", { hour: "2-digit", minute: "2-digit" }).format(d);

function Cornice({ testId, titolo, children, azioni, codice }) {
  return (
    <div data-testid={testId} className="flex min-h-screen flex-col bg-page">
      <header className="flex items-center justify-between px-6 py-5 sm:px-10 sm:py-7">
        <LogoIdA className="w-40 text-brand sm:w-[184px]" aria-label="Interni D'Autore Arredamenti" />
        <PoweredBy />
      </header>
      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-4 sm:items-center sm:px-10 sm:pb-24">
        <section role="alert" aria-labelledby="errore-sistema-titolo" className="flex w-full max-w-[520px] animate-ida-rise flex-col gap-3.5 rounded-xl border border-line bg-surface p-6 sm:p-8">
          <span aria-hidden="true" className="inline-flex size-[52px] items-center justify-center rounded-lg bg-warning-bg text-warning">
            <CircleAlert className="size-7" strokeWidth={1.75} />
          </span>
          <h1 id="errore-sistema-titolo" className="font-display text-h1-mobile text-ink-strong">{titolo}</h1>
          <div className="flex flex-col gap-3.5 text-ink-2">{children}</div>
          {azioni ? <div className="flex flex-wrap gap-2.5 pt-1.5 max-sm:flex-col">{azioni}</div> : null}
          {codice ? <p className="mt-1.5 border-t border-line-subtle pt-3.5 text-small tabular-nums text-ink-2">Codice per l&#39;assistenza: <span className="font-semibold text-ink">{codice}</span></p> : null}
        </section>
      </main>
    </div>
  );
}

// L'ora dell'ultimo «Riprova»: la pagina sparisce mentre ritenta e ricompare se non è andata (resta finché la scheda è aperta).
let ultimoTentativo = null;

/** Il ruolo o il codice di verifica non arrivano: «Riprova» li chiede di nuovo. */
export function ProfiloNonDisponibile({ profilo, mfa, onRiprova, onEsci }) {
  const adesso = new Date();
  const soloCodice = !profilo && mfa;
  return (
    <Cornice
      testId="auth-profilo-non-disponibile"
      titolo={soloCodice ? "Non riusciamo a controllare il tuo codice di verifica" : "Non riusciamo a capire chi sei"}
      codice={`${soloCodice ? "VERIFICA" : "PROFILO"} · ${giornoEOra(adesso)}`}
      azioni={
        <>
          <Button
            size="lg"
            data-testid="profilo-riprova"
            onClick={() => {
              ultimoTentativo = new Date();
              onRiprova();
            }}
          >
            Riprova
          </Button>
          <Button size="lg" variant="secondary" data-testid="profilo-esci" onClick={onEsci}>
            Esci e rientra
          </Button>
        </>
      }
    >
      <p>
        {soloCodice
          ? "Sei collegato, ma il gestionale non riesce a sapere se hai il codice di verifica, e senza quello non può farti entrare."
          : "Il gestionale non riesce a leggere il tuo profilo, e senza profilo non sa cosa farti vedere."}{" "}
        Di solito basta riprovare dopo pochi secondi.
      </p>
      {ultimoTentativo && Date.now() - ultimoTentativo.getTime() < 120000 ? (
        <p role="status" className="rounded-md bg-warning-bg px-3 py-2.5 font-semibold tabular-nums text-warning-ink">
          Ancora niente alle {soloOra(ultimoTentativo)}. Riprova tra qualche secondo, o esci e rientra.
        </p>
      ) : null}
    </Cornice>
  );
}

/** Un ruolo senza nessuna sezione assegnata. */
export function SenzaSezioni({ onEsci }) {
  return (
    <Cornice
      testId="guardia-senza-sezioni"
      titolo="Il tuo profilo non ha ancora sezioni"
      azioni={
        <Button size="lg" variant="secondary" onClick={onEsci}>
          Esci
        </Button>
      }
    >
      <p>Sei entrato, ma al tuo ruolo non è assegnata nessuna parte del gestionale. Chiedi al titolare di controllare il tuo ruolo in Impostazioni.</p>
    </Cornice>
  );
}
